<template>
    <el-dialog v-model="dialogVisible" title="会话附件" width="720px" :close-on-click-modal="false"
        class="attachment-list-dialog">
        <div class="attach-toolbar">
            <span class="attach-hint">
                展示当前会话所有 AI 生成的附件
                <span v-if="props.sessionId" class="session-tag">会话ID: {{ props.sessionId }}</span>
                <span v-else class="session-tag warn">未选择会话</span>
            </span>
            <el-select v-model="attachFormat" placeholder="格式" clearable size="small"
                style="width: 120px" @change="fetchAttachments">
                <el-option label="全部" value="" />
                <el-option label="docx" value="docx" />
                <el-option label="xlsx" value="xlsx" />
                <el-option label="pdf" value="pdf" />
            </el-select>
            <el-button size="small" :icon="Refresh" @click="fetchAttachments">刷新</el-button>
        </div>

        <div class="attach-table-wrapper" v-loading="attachLoading">
            <el-table :data="attachmentList" stripe size="small" empty-text="暂无附件">
                <el-table-column prop="fileName" label="文件名" min-width="200" show-overflow-tooltip />
                <el-table-column prop="attachType" label="格式" width="80" align="center">
                    <template #default="{ row }">
                        <span class="fmt-tag" :class="'fmt-' + (row.attachType || '').toLowerCase()">
                            {{ (row.attachType || '').toUpperCase() }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column label="大小" width="90" align="right">
                    <template #default="{ row }">
                        {{ formatSize(row.fileSize) }}
                    </template>
                </el-table-column>
                <el-table-column prop="createdAt" label="生成时间" width="160">
                    <template #default="{ row }">
                        {{ formatTime(row.createdAt) }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="160" align="center" fixed="right">
                    <template #default="{ row }">
                        <el-button type="primary" link size="small" @click="downloadAttach(row)">
                            下载
                        </el-button>
                        <el-button type="danger" link size="small" @click="removeAttach(row)">
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <div class="pagination-wrap" v-if="attachTotal > 0">
            <el-pagination v-model:current-page="attachPageNum" v-model:page-size="attachPageSize"
                :total="attachTotal" :page-sizes="[10, 20, 50]"
                layout="total, sizes, prev, pager, next"
                @size-change="handleAttachSizeChange" @current-change="handleAttachPageChange" />
        </div>
    </el-dialog>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { ElMessageBox, ElMessage } from 'element-plus';
import { Refresh } from '@element-plus/icons-vue';
import { listAttachmentsBySession, removeAttachment } from '@/api/ai/chat';
import { getToken } from '@/utils/auth';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    sessionId: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:modelValue']);

const dialogVisible = ref(false);

// 附件列表状态
const attachLoading = ref(false);
const attachmentList = ref([]);
const attachFormat = ref('');
const attachPageNum = ref(1);
const attachPageSize = ref(20);
const attachTotal = ref(0);
const attachPollTimer = ref(null);

watch(() => props.modelValue, (val) => {
    dialogVisible.value = val;
    if (val) {
        // 每次打开重置筛选和分页
        attachFormat.value = '';
        attachPageNum.value = 1;
        fetchAttachments();
        startAttachPoll();
    } else {
        stopAttachPoll();
    }
});

watch(dialogVisible, (val) => {
    emit('update:modelValue', val);
});

// sessionId 变化时,若对话框处于打开状态,刷新一次列表
watch(() => props.sessionId, () => {
    if (dialogVisible.value) {
        attachPageNum.value = 1;
        fetchAttachments();
    }
});

async function fetchAttachments() {
    if (!props.sessionId) {
        attachmentList.value = [];
        attachTotal.value = 0;
        return;
    }
    attachLoading.value = true;
    try {
        const res = await listAttachmentsBySession(props.sessionId, {
            pageNum: attachPageNum.value,
            pageSize: attachPageSize.value,
            attachType: attachFormat.value
        });
        if (res?.code === 200) {
            attachmentList.value = res.rows || [];
            attachTotal.value = res.total || 0;
        } else {
            attachmentList.value = [];
            attachTotal.value = 0;
        }
    } catch (error) {
        console.error('加载附件失败:', error);
    } finally {
        attachLoading.value = false;
    }
}

function handleAttachSizeChange() {
    attachPageNum.value = 1;
    fetchAttachments();
}

function handleAttachPageChange() {
    fetchAttachments();
}

function startAttachPoll() {
    stopAttachPoll();
    // 没会话 ID 则不轮询
    if (!props.sessionId) return;
    attachPollTimer.value = setInterval(() => {
        // 仅在对话框打开 + 有 sessionId 才轮询
        if (!dialogVisible.value || !props.sessionId) {
            stopAttachPoll();
            return;
        }
        // 始终查第一页(因为轮询是看新生成的)
        fetchAttachments();
    }, 5000);
}

function stopAttachPoll() {
    if (attachPollTimer.value) {
        clearInterval(attachPollTimer.value);
        attachPollTimer.value = null;
    }
}

/**
 * 下载附件:走 fetch + Authorization 头拿 blob,然后创建临时 a 标签触发下载
 * 与 FileAttachmentMessage.vue 中的 downloadFile 保持一致
 */
async function downloadAttach(row) {
    try {
        const baseApi = import.meta.env.VITE_APP_BASE_API || '';
        const url = baseApi + '/file/privateDownload/' + row.fileId;
        const token = getToken();
        const res = await fetch(url, {
            method: 'GET',
            headers: token ? { 'Authorization': token } : {}
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        let downloadName = row.fileName || ('report_' + row.fileId);
        const disposition = res.headers.get('Content-Disposition') || '';
        const m = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
        if (m && m[1]) {
            try { downloadName = decodeURIComponent(m[1]); } catch (e) { downloadName = m[1]; }
        }
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = downloadName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
        ElMessage.error('下载失败: ' + (e.message || '未知错误'));
    }
}

async function removeAttach(row) {
    try {
        await ElMessageBox.confirm(
            `确定要删除附件"${row.fileName}"吗?该操作不会删除 FileStorage 中的源文件,只是从当前会话附件列表移除。`,
            '删除附件',
            {
                confirmButtonText: '确定删除',
                cancelButtonText: '取消',
                type: 'warning'
            }
        );
        const res = await removeAttachment(row.id);
        if (res?.code === 200) {
            ElMessage.success('删除成功');
            fetchAttachments();
        } else {
            ElMessage.error(res?.msg || '删除失败');
        }
    } catch (error) {
        if (error !== 'cancel') {
            console.error('删除附件失败:', error);
        }
    }
}

onUnmounted(() => {
    stopAttachPoll();
});

function formatSize(bytes) {
    if (!bytes || bytes <= 0) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function formatTime(time) {
    if (!time) return '未知时间';
    const date = new Date(time);
    if (isNaN(date.getTime())) return '未知时间';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}-${day} ${hours}:${minutes}`;
}
</script>

<style lang="scss" scoped>
.attach-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.attach-hint {
    font-size: 12px;
    color: #999;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 6px;
}

.session-tag {
    padding: 1px 8px;
    background: #e6f7ff;
    color: #1890ff;
    border: 1px solid #91d5ff;
    border-radius: 10px;
    font-size: 11px;
    font-family: monospace;

    &.warn {
        background: #fff7e6;
        color: #fa8c16;
        border-color: #ffd591;
    }
}

.attach-table-wrapper {
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    overflow: hidden;
}

.pagination-wrap {
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
}

.fmt-tag {
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 600;

    &.fmt-docx {
        background: #e6f7ff;
        color: #1890ff;
        border: 1px solid #91d5ff;
    }

    &.fmt-xlsx {
        background: #f6ffed;
        color: #52c41a;
        border: 1px solid #b7eb8f;
    }

    &.fmt-pdf {
        background: #fff7e6;
        color: #fa8c16;
        border: 1px solid #ffd591;
    }
}
</style>
